/**
 * MailCompanionPanel — Admin configuration for Mail Companion feature.
 *
 * Two sections:
 * 1. Receiving Email Account: setup/status of the IMAP account EveMind reads from
 * 2. Company Domain Mapping: per-company enable/disable + sender domain config
 */
import { useState, useMemo } from "react";
import { CheckCircle, AlertCircle, XCircle, Plus, Trash01, RefreshCw01, SearchMd } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { Toggle } from "@/components/base/toggle/toggle";
import { Input } from "@/components/base/input/input";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Select } from "@/components/base/select/select";
import { toast } from "@/lib/toast-service";
import { apiPost, apiPut } from "@/lib/api-client";
import { cx } from "@/utils/cx";
import { useEmailAccounts, useTestEmailConnection } from "@/hooks/api/use-email-accounts";
import { useCompanies } from "@/hooks/api/use-companies";
import { useCompanySettings } from "@/hooks/api/use-company-settings";
import { useQueryClient } from "@tanstack/react-query";
import type { EmailAccountResponse } from "@/types/api/email";
import type { CompanyResponse } from "@/types/api/admin";

const PAGE_SIZE = 10;

// Domains that cannot be used as sender domains (too generic, would match unrelated emails)
const BLOCKED_DOMAINS = new Set([
    "gmail.com", "googlemail.com", "outlook.com", "hotmail.com", "live.com",
    "yahoo.com", "yahoo.it", "yahoo.co.uk", "aol.com", "icloud.com", "me.com",
    "mail.com", "protonmail.com", "proton.me", "zoho.com",
]);

function validateDomains(domains: string[]): string | null {
    const blocked = domains.filter(d => BLOCKED_DOMAINS.has(d.toLowerCase().trim()));
    if (blocked.length > 0) {
        return `Generic email domains not allowed: ${blocked.join(", ")}. Use company-specific domains.`;
    }
    return null;
}

/** Build query string with repeated keys for arrays (FastAPI List[str] format) */
function buildCompanySettingsUrl(companyId: string, data: Record<string, unknown>): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(data)) {
        if (value === undefined || value === null) continue;
        if (Array.isArray(value)) {
            for (const item of value) {
                params.append(key, String(item));
            }
        } else {
            params.append(key, String(value));
        }
    }
    return `/companies/${companyId}/settings${params.toString() ? `?${params.toString()}` : ""}`;
}

// ============================================================================
// Helpers
// ============================================================================

function formatRelativeTime(dateString?: string | null): string {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 1000 / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
}

// ============================================================================
// Account Status Badge
// ============================================================================

function AccountStatusBadge({ status }: { status: string }) {
    const map: Record<string, { color: "success" | "blue" | "error" | "warning" | "gray"; icon: typeof CheckCircle; label: string }> = {
        active: { color: "success", icon: CheckCircle, label: "Active" },
        syncing: { color: "blue", icon: RefreshCw01, label: "Syncing" },
        error: { color: "error", icon: XCircle, label: "Error" },
        authentication_required: { color: "warning", icon: AlertCircle, label: "Auth Required" },
    };
    const cfg = map[status] ?? { color: "gray" as const, icon: AlertCircle, label: status };
    const Icon = cfg.icon;
    return (
        <Badge color={cfg.color} size="sm">
            <Icon className={cx("w-3 h-3 mr-1", status === "syncing" && "animate-spin")} />
            {cfg.label}
        </Badge>
    );
}

// ============================================================================
// Account Setup Form
// ============================================================================

interface SetupFormData {
    email_address: string;
    display_name: string;
    imap_host: string;
    imap_port: number;
    imap_username: string;
    imap_password: string;
    imap_use_ssl: boolean;
    smtp_host: string;
    smtp_port: number;
}

const INITIAL_FORM: SetupFormData = {
    email_address: "",
    display_name: "Mail Companion",
    imap_host: "imap.gmail.com",
    imap_port: 993,
    imap_username: "",
    imap_password: "",
    imap_use_ssl: true,
    smtp_host: "smtp.gmail.com",
    smtp_port: 587,
};

function AccountSetupForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
    const [form, setForm] = useState<SetupFormData>(INITIAL_FORM);
    const [saving, setSaving] = useState(false);

    const set = <K extends keyof SetupFormData>(key: K, value: SetupFormData[K]) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const handleSubmit = async () => {
        if (!form.email_address || !form.imap_host || !form.imap_password) {
            toast.error("Email address, IMAP host, and password are required.");
            return;
        }
        setSaving(true);
        try {
            await apiPost("/email/accounts/setup-mail-companion", {
                email_address: form.email_address,
                display_name: form.display_name,
                imap_host: form.imap_host,
                imap_port: form.imap_port,
                imap_username: form.imap_username || form.email_address,
                imap_password: form.imap_password,
                imap_use_ssl: form.imap_use_ssl,
                smtp_host: form.smtp_host || undefined,
                smtp_port: form.smtp_port,
                sender_domains: [],
                folders_to_watch: ["INBOX"],
            });
            toast.success("Mail Companion account created.");
            onSuccess();
        } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : "Setup failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4 p-4 border border-secondary rounded-lg bg-secondary/30">
            <h4 className="text-sm font-semibold text-primary">Set Up Mail Companion Account</h4>

            <div className="grid grid-cols-2 gap-3">
                <Input label="Email Address" value={form.email_address} onChange={v => set("email_address", v)} placeholder="eve.mind.ai@gmail.com" />
                <Input label="Display Name" value={form.display_name} onChange={v => set("display_name", v)} placeholder="Mail Companion" />
            </div>

            <div className="grid grid-cols-3 gap-3">
                <Input label="IMAP Host" value={form.imap_host} onChange={v => set("imap_host", v)} placeholder="imap.gmail.com" />
                <Input label="IMAP Port" type="number" value={String(form.imap_port)} onChange={v => set("imap_port", Number(v))} />
                <Input label="IMAP Password" type="password" value={form.imap_password} onChange={v => set("imap_password", v)} placeholder="App password" />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Input label="SMTP Host (optional)" value={form.smtp_host} onChange={v => set("smtp_host", v)} placeholder="smtp.gmail.com" />
                <Input label="SMTP Port" type="number" value={String(form.smtp_port)} onChange={v => set("smtp_port", Number(v))} />
            </div>

            <div className="flex items-center gap-2">
                <Toggle isSelected={form.imap_use_ssl} onChange={v => set("imap_use_ssl", v)} />
                <span className="text-sm text-secondary">Use SSL (port 993)</span>
            </div>

            <div className="flex justify-end gap-2 pt-1">
                <Button color="secondary" size="sm" onClick={onCancel}>Cancel</Button>
                <Button color="primary" size="sm" onClick={handleSubmit} isLoading={saving}>Save Account</Button>
            </div>
        </div>
    );
}

// ============================================================================
// Account Card
// ============================================================================

function AccountCard({ account }: { account: EmailAccountResponse }) {
    const testConnection = useTestEmailConnection();

    const handleTest = async () => {
        try {
            const result = await testConnection.mutateAsync(account.account_id);
            toast[result.success ? "success" : "error"](result.success ? "Connection OK" : (result.error_message || "Failed"));
        } catch {
            toast.error("Connection test failed.");
        }
    };

    return (
        <div className="p-4 border border-secondary rounded-lg bg-primary flex items-center justify-between gap-4">
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-primary text-sm">{account.email_address}</span>
                    <AccountStatusBadge status={account.status} />
                </div>
                <p className="text-xs text-tertiary mt-0.5">
                    Provider: {account.provider} &middot; Last sync: {formatRelativeTime(account.last_sync_at)}
                </p>
            </div>
            <Button color="secondary" size="sm" onClick={handleTest} isLoading={testConnection.isPending} className="whitespace-nowrap shrink-0">
                Test Connection
            </Button>
        </div>
    );
}

// ============================================================================
// Company Row
// ============================================================================

function CompanyRow({ company }: { company: CompanyResponse }) {
    const { data: settings, isLoading } = useCompanySettings(company.company_id);
    const queryClient = useQueryClient();
    const [expanded, setExpanded] = useState(false);
    const [domains, setDomains] = useState<string[]>([]);
    const [notifyEvents, setNotifyEvents] = useState<string[]>([]);
    const [locale, setLocale] = useState("en");
    const [dirty, setDirty] = useState(false);

    const enabled = settings?.mail_companion_enabled ?? false;
    const displayDomains = settings?.mail_companion_sender_domains?.filter(Boolean);

    const handleExpand = () => {
        if (!expanded && settings) {
            setDomains(settings.mail_companion_sender_domains?.length ? settings.mail_companion_sender_domains : [""]);
            setNotifyEvents(settings.mail_companion_notify_events ?? ["document_issue", "tracking_update", "weekly_digest"]);
            setLocale(settings.mail_companion_reply_locale ?? "en");
            setDirty(false);
        }
        setExpanded(!expanded);
    };

    const handleToggle = async (val: boolean) => {
        try {
            const url = buildCompanySettingsUrl(company.company_id, { mail_companion_enabled: val });
            await apiPut(url, undefined);
            queryClient.invalidateQueries({ queryKey: ["company-settings", company.company_id] });
            toast.success(`${val ? "Enabled" : "Disabled"} for ${company.company_name}`);
        } catch { toast.error("Failed to update."); }
    };

    const handleSave = async () => {
        const cleanDomains = domains.filter(d => d.trim());
        const validationError = validateDomains(cleanDomains);
        if (validationError) {
            toast.error(validationError);
            return;
        }
        try {
            const url = buildCompanySettingsUrl(company.company_id, {
                mail_companion_enabled: true,
                mail_companion_sender_domains: cleanDomains,
                mail_companion_notify_events: notifyEvents,
                mail_companion_reply_locale: locale,
            });
            await apiPut(url, undefined);
            queryClient.invalidateQueries({ queryKey: ["company-settings", company.company_id] });
            toast.success(`Saved for ${company.company_name}`);
            setDirty(false);
            setExpanded(false);
        } catch { toast.error("Failed to save."); }
    };

    const addDomain = () => { setDomains([...domains, ""]); setDirty(true); };
    const updateDomain = (i: number, v: string) => { const u = [...domains]; u[i] = v.toLowerCase().trim(); setDomains(u); setDirty(true); };
    const removeDomain = (i: number) => { setDomains(domains.filter((_, idx) => idx !== i)); setDirty(true); };
    const toggleEvent = (e: string) => { setNotifyEvents(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]); setDirty(true); };

    if (isLoading) return <div className="px-4 py-3 animate-pulse bg-secondary/20 h-12" />;

    return (
        <div className="border-b border-secondary/50 last:border-b-0">
            {/* Summary row */}
            <div
                className={cx("grid grid-cols-[1fr_200px_80px] items-center px-4 py-2.5 cursor-pointer hover:bg-secondary/30 transition-colors", expanded && "bg-secondary/30")}
                onClick={handleExpand}
            >
                <span className="font-medium text-sm text-primary truncate">{company.company_name}</span>
                <span className="text-xs text-tertiary truncate">
                    {displayDomains?.length ? displayDomains.join(", ") : <span className="text-quaternary italic">No domains</span>}
                </span>
                <div className="flex justify-end" onClick={e => e.stopPropagation()}>
                    <Toggle isSelected={enabled} onChange={handleToggle} />
                </div>
            </div>

            {/* Expanded edit */}
            {expanded && (
                <div className="px-4 pb-4 pt-2 space-y-3 bg-secondary/20">
                    <div>
                        <label className="block text-xs font-medium text-secondary mb-1.5">Sender Domains</label>
                        <div className="space-y-1.5">
                            {domains.map((domain, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <Input value={domain} onChange={(v: string) => updateDomain(i, v)} placeholder="example.com" className="flex-1" />
                                    <button onClick={() => removeDomain(i)} className="text-tertiary hover:text-error p-1 shrink-0">
                                        <Trash01 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            <button onClick={addDomain} className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent-hover">
                                <Plus className="w-3 h-3" /> Add domain
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-secondary mb-1.5">Notify Events</label>
                        <div className="flex flex-wrap gap-4">
                            {["document_issue", "tracking_update", "weekly_digest"].map(event => (
                                <Checkbox
                                    key={event}
                                    isSelected={notifyEvents.includes(event)}
                                    onChange={() => toggleEvent(event)}
                                    size="sm"
                                    label={event.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="w-48">
                        <Select
                            label="Reply Locale"
                            selectedKey={locale}
                            onSelectionChange={key => { setLocale(String(key)); setDirty(true); }}
                            items={[
                                { id: "en", label: "English" },
                                { id: "it", label: "Italiano" },
                                { id: "es", label: "Espanol" },
                                { id: "fr", label: "Francais" },
                            ]}
                            size="sm"
                        >
                            {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
                        </Select>
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                        <Button color="secondary" size="sm" onClick={() => setExpanded(false)}>Cancel</Button>
                        <Button color="primary" size="sm" onClick={handleSave} isDisabled={!dirty}>Save</Button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// Main Panel
// ============================================================================

export function MailCompanionPanel() {
    const { data: accounts, isLoading: accountsLoading, refetch: refetchAccounts } = useEmailAccounts();
    const { data: companies, isLoading: companiesLoading } = useCompanies({ is_active: true, limit: 500 });
    const queryClient = useQueryClient();
    const [showSetup, setShowSetup] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);

    // Find mail companion accounts
    const mcAccounts = (accounts ?? []).filter(
        a => a.settings && (a.settings as Record<string, unknown>).mode === "mail_companion"
    );

    // Filter & paginate companies
    const filteredCompanies = useMemo(() => {
        const list = companies ?? [];
        if (!search.trim()) return list;
        const q = search.toLowerCase();
        return list.filter(c =>
            c.company_name.toLowerCase().includes(q) ||
            c.primary_email?.toLowerCase().includes(q)
        );
    }, [companies, search]);

    const totalPages = Math.ceil(filteredCompanies.length / PAGE_SIZE);
    const pagedCompanies = filteredCompanies.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const handleSetupSuccess = () => {
        setShowSetup(false);
        refetchAccounts();
        queryClient.invalidateQueries({ queryKey: ["company-settings"] });
    };

    return (
        <div className="space-y-8">
            {/* Section 1: Receiving Email Account */}
            <section>
                <h3 className="text-sm font-semibold text-primary mb-1">Receiving Email Account</h3>
                <p className="text-xs text-tertiary mb-3">
                    The IMAP inbox EveMind reads for incoming mail companion emails.
                    Clients send documents to this address with container numbers in the subject.
                </p>

                {accountsLoading ? (
                    <div className="text-sm text-tertiary animate-pulse">Loading...</div>
                ) : mcAccounts.length > 0 ? (
                    <div className="space-y-2">
                        {mcAccounts.map(a => <AccountCard key={a.account_id} account={a} />)}
                    </div>
                ) : showSetup ? (
                    <AccountSetupForm onSuccess={handleSetupSuccess} onCancel={() => setShowSetup(false)} />
                ) : (
                    <div className="p-6 border border-dashed border-secondary rounded-lg text-center">
                        <p className="text-sm text-tertiary mb-3">No mail companion account configured.</p>
                        <Button color="secondary" size="sm" onClick={() => setShowSetup(true)}>
                            Set Up Account
                        </Button>
                    </div>
                )}
            </section>

            {/* Section 2: Company Domain Mapping */}
            <section>
                <h3 className="text-sm font-semibold text-primary mb-1">Company Domain Mapping</h3>
                <p className="text-xs text-tertiary mb-3">
                    Map sender domains to companies. When an email arrives from a mapped domain, it is routed to that company.
                    Click a row to configure domains and notifications.
                </p>

                {companiesLoading ? (
                    <div className="text-sm text-tertiary animate-pulse">Loading companies...</div>
                ) : (
                    <div className="border border-secondary rounded-lg overflow-hidden">
                        {/* Search */}
                        <div className="px-3 py-2 bg-secondary/30 border-b border-secondary">
                            <div className="relative">
                                <SearchMd className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary pointer-events-none" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={e => { setSearch(e.target.value); setPage(0); }}
                                    placeholder="Search companies..."
                                    className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-secondary bg-primary text-primary placeholder:text-quaternary focus:border-accent focus:ring-1 focus:ring-accent"
                                />
                            </div>
                        </div>

                        {/* Header */}
                        <div className="grid grid-cols-[1fr_200px_80px] items-center px-4 py-2 bg-secondary/50 text-xs font-medium text-tertiary uppercase tracking-wider border-b border-secondary">
                            <span>Company</span>
                            <span>Domains</span>
                            <span className="text-right">Enabled</span>
                        </div>

                        {/* Rows */}
                        {pagedCompanies.length > 0 ? (
                            pagedCompanies.map(c => <CompanyRow key={c.company_id} company={c} />)
                        ) : (
                            <div className="px-4 py-8 text-center text-sm text-tertiary">
                                {search ? "No companies match your search." : "No active companies found."}
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-4 py-2 bg-secondary/30 border-t border-secondary text-xs text-tertiary">
                                <span>
                                    {filteredCompanies.length} companies &middot; Page {page + 1} of {totalPages}
                                </span>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => setPage(p => Math.max(0, p - 1))}
                                        disabled={page === 0}
                                        className="px-2 py-1 rounded border border-secondary hover:bg-secondary/50 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Prev
                                    </button>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                        disabled={page >= totalPages - 1}
                                        className="px-2 py-1 rounded border border-secondary hover:bg-secondary/50 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
}
